/**
 * API Helper
 * Unified fetch wrapper with error handling, CSRF token, and response parsing
 */

import { getCsrfToken } from './csrf.js';
import { showToast } from './ui/toast.js';

/**
 * Default request options
 */
const defaultOptions = {
  credentials: 'same-origin',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
};

/**
 * API Error class for consistent error handling
 */
export class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Make an API request
 * @param {string} url - Request URL
 * @param {object} options - Fetch options
 * @param {object} config - Additional configuration
 * @param {boolean} config.showErrorToast - Show toast on error (default: true)
 * @param {boolean} config.includeCredentials - Include credentials (default: true)
 * @returns {Promise<any>} Response data (JSON or text)
 */
export async function api(url, options = {}, config = {}) {
  const { showErrorToast = true, includeCredentials = true } = config;

  // Merge headers
  const headers = {
    ...defaultOptions.headers,
    ...options.headers,
  };

  // Add CSRF token for non-GET requests
  if (options.method && options.method.toUpperCase() !== 'GET') {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRFToken'] = csrfToken;
    }
  }

  // If body is object and not FormData, stringify it
  let { body } = options;
  if (body && typeof body === 'object' && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, {
      ...defaultOptions,
      ...options,
      credentials: includeCredentials ? 'same-origin' : 'omit',
      headers,
      body,
    });

    // Parse response based on content type
    const contentType = response.headers.get('content-type');
    let data;

    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Handle non-OK responses
    if (!response.ok) {
      const errorMessage =
        typeof data === 'object' ? data.message || data.error || 'Request failed' : 'Request failed';

      if (showErrorToast) {
        showToast(errorMessage, 'error');
      }

      throw new ApiError(errorMessage, response.status, data);
    }

    return data;
  } catch (error) {
    // Re-throw ApiError as-is
    if (error instanceof ApiError) {
      throw error;
    }

    // Network error or other fetch error
    const message = error.message || 'Network error';
    if (showErrorToast) {
      showToast(message, 'error');
    }

    throw new ApiError(message, 0, null);
  }
}
